import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a date string as local midnight, handling both "YYYY-MM-DD" and
 * full ISO timestamps like "2026-02-18T08:00:00.000Z". Avoids the JS
 * pitfall where `new Date("2026-02-18")` parses as UTC midnight and
 * displays as the previous day in western timezones.
 */
export function parseLocalDate(dateStr: string): Date {
  const dateOnly = dateStr.slice(0, 10);
  return new Date(dateOnly + 'T00:00:00');
}

/** YYYY-MM-DD for the user's local calendar (matches `date +%Y-%m-%d` in their timezone). */
export function getLocalCalendarDate(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** IANA timezone from the browser (e.g. America/Vancouver). Empty if unavailable. */
export function getClientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** Midnight of the current local day. */
export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Check overdue / due-soon status for a date-only string. */
export function dueDateStatus(dueOn: string | null | undefined) {
  if (!dueOn) return { isOverdue: false, isDueSoon: false };
  const due = parseLocalDate(dueOn);
  const today = startOfToday();
  const sevenDays = new Date(today);
  sevenDays.setDate(sevenDays.getDate() + 7);
  return {
    isOverdue: due < today,
    isDueSoon: due >= today && due <= sevenDays,
  };
}

